import type { Doc } from "@convex/_generated/dataModel";
import type { PriceWithProduct } from "@convex/billing";
import { useContext } from "react";
import { PriceMetadata } from "@/server/database/products";
import { SubscriptionContext, SubscriptionPricesContext } from "./contexts";
import {
  hasActivePremiumSubscription,
  hasActiveSubscription,
} from "./subscription";

export const useSubscription = () => {
  const subscription = useContext(SubscriptionContext);

  return {
    subscription,
    hasActiveSubscription: hasActiveSubscription(
      subscription?.subscription ?? null,
    ),
    hasActivePremiumSubscription: hasActivePremiumSubscription(
      subscription?.subscription ?? null,
    ),
    isTrial: subscription?.subscription.status === "trialing",
  };
};

// Tier hierarchy (sorted by discount rate, lowest to highest)
const TIER_HIERARCHY = [
  { tier: "standard", interval: "month" },
  { tier: "standard", interval: "year" },
  { tier: "premium", interval: "month" },
  { tier: "premium", interval: "year" },
] as const;

type TierConfig = (typeof TIER_HIERARCHY)[number];

interface UpgradeHint {
  targetTier: string;
  targetInterval: string;
  targetDiscount: number;
  targetPriceLookupKey: string;
}

// Type for price with metadata (can be with or without products join)
type PriceWithMetadata = Pick<Doc<"prices">, "metadata" | "lookupKey">;

/**
 * Get the discount rate from a price's metadata.
 */
function getDiscountRateFromPrice(
  price: PriceWithMetadata | null | undefined,
): number {
  if (!price?.metadata) return 0;
  const parsed = PriceMetadata.safeParse(price.metadata);
  if (!parsed.success || !parsed.data.credits_discount_rate) return 0;
  return Number.parseInt(parsed.data.credits_discount_rate, 10);
}

/**
 * Find the subscription price that matches the tier and interval.
 */
function findPriceByTierAndInterval(
  prices: PriceWithProduct[] | null,
  tier: string,
  interval: string,
): PriceWithProduct | undefined {
  if (!prices) return undefined;
  return prices.find((p) => {
    const lookupKey = p.price.lookupKey?.toLowerCase() ?? "";
    return lookupKey.includes(tier) && lookupKey.includes(interval);
  });
}

/**
 * Get the upgrade hint for the next tier in the hierarchy.
 */
function getUpgradeHint(
  currentTier: string | null,
  currentInterval: string | null | undefined,
  prices: PriceWithProduct[] | null,
): UpgradeHint | null {
  if (!currentTier || !currentInterval || !prices) return null;

  // Normalize interval
  const normalizedInterval = currentInterval === "year" ? "year" : "month";

  // Find current position in hierarchy
  const currentIndex = TIER_HIERARCHY.findIndex(
    (h) => h.tier === currentTier && h.interval === normalizedInterval,
  );

  if (currentIndex === -1 || currentIndex === TIER_HIERARCHY.length - 1) {
    return null; // Unknown tier or already at highest
  }

  // Get next tier in hierarchy
  const nextTierConfig: TierConfig = TIER_HIERARCHY[currentIndex + 1];

  // Find the price for the next tier to get the actual discount rate
  const nextPrice = findPriceByTierAndInterval(
    prices,
    nextTierConfig.tier,
    nextTierConfig.interval,
  );

  const nextDiscount = getDiscountRateFromPrice(nextPrice?.price);
  if (nextDiscount === 0 || !nextPrice?.price.lookupKey) return null;

  return {
    targetTier: nextTierConfig.tier,
    targetInterval: nextTierConfig.interval,
    targetDiscount: nextDiscount,
    targetPriceLookupKey: nextPrice.price.lookupKey,
  };
}

/**
 * Get the maximum discount rate from all subscription prices.
 */
function getMaxDiscount(prices: PriceWithProduct[] | null): number {
  if (!prices) return 0;
  let maxDiscount = 0;
  for (const price of prices) {
    const discount = getDiscountRateFromPrice(price.price);
    if (discount > maxDiscount) {
      maxDiscount = discount;
    }
  }
  return maxDiscount;
}

/**
 * Hook to get the subscription discount rate for extra credit purchases.
 * Returns the discount percentage, tier info, upgrade hints, and max discount.
 *
 * Note: Trial subscribers do NOT get the discount - it's a benefit for paying subscribers only.
 */
export const useSubscriptionDiscount = () => {
  const subscription = useContext(SubscriptionContext);
  const subscriptionPrices = useContext(SubscriptionPricesContext);

  // Calculate max discount for non-subscribers
  const maxDiscount = getMaxDiscount(subscriptionPrices);

  const isTrial = subscription?.subscription.status === "trialing";

  // No subscription or trial subscription means no discount
  // Trial users don't get the discount - it's a benefit for paying subscribers only
  if (
    !subscription ||
    !hasActiveSubscription(subscription.subscription) ||
    isTrial
  ) {
    return {
      hasDiscount: false,
      discountRate: 0,
      tierName: null,
      interval: null,
      upgradeHint: null,
      maxDiscount,
      isTrial,
    };
  }

  // Parse the price metadata to get the discount rate
  const discountRate = getDiscountRateFromPrice(subscription.price);
  const tierName = subscription.subscription.tier;
  const interval = subscription.price?.interval ?? null;

  // Calculate upgrade hint based on tier hierarchy
  const upgradeHint = getUpgradeHint(tierName, interval, subscriptionPrices);

  return {
    hasDiscount: discountRate > 0,
    discountRate,
    tierName,
    interval,
    upgradeHint,
    maxDiscount,
    isTrial,
  };
};
